import * as THREE from 'three';
import { SwordTracker, type SwordFrame } from '../sword/tracking';
import {
  DIFFICULTY,
  DURATION_MODE,
  SWORD_BLADE_LENGTH_M,
  SWORD_GRIP_LENGTH_M,
  TARGETS_FIRST_SPAWN_DELAY_MS,
  TARGETS_HIT_ANIM_MS,
  TARGETS_HIT_ANIM_SCALE,
  TARGETS_MISS_ANIM_MS,
  TARGETS_MISS_COLOR_HEX,
  TIMER_STATE,
  type Difficulty,
  type DurationMode,
  type Zone,
} from '../config/constants';
import type { BladePose } from '../hit-detection/sweep';
import { DrillRuntimeBase } from './drillRuntime';
import {
  captureSpawnAnchor,
  computeSpawnPosition,
  sampleZone,
  type SpawnAnchor,
} from '../targets/spawn';
import { disposeTargetMesh } from '../targets/mesh';
import { OffscreenIndicator } from '../feedback/offscreenIndicator';
import type { SessionStats } from './stats';

/** Scratch for the camera world position in `faceCamera`; transient, never retained. */
const _camPos = new THREE.Vector3();

export type TargetDrillOptions = {
  scene: THREE.Scene;
  camera: THREE.Camera;
  sword: THREE.Object3D;
  swordController: THREE.Object3D;
  offHandController: THREE.Object3D;
  durationMs: number;
  userHeightCm: number;
  /** timed = countdown + TTL misses; free = no timer, targets never auto-miss. */
  durationMode: DurationMode;
  /**
   * Difficulty the run is played at (#43). `custom` and free runs are non-competitive: not
   * persisted, no best recorded, no leaderboard. Carried into the stats for display regardless.
   */
  difficulty: Difficulty;
  /** Multiplier on the disc radius (mesh + hit). */
  sizeFactor: number;
  /** Radial spawn distance from the user (m). */
  distanceM: number;
  /** Time (ms) a target stays alive before auto-missing (timed mode only). */
  ttlMs: number;
  /** Delay (ms) between a target resolving and the next spawn. */
  spawnDelayMs: number;
  onEnd: (stats: SessionStats) => void;
};

/** The live target as seen by a subclass's detection / scoring (a read view of the alive phase). */
export type AliveTarget<TData> = {
  group: THREE.Group;
  center: THREE.Vector3;
  zone: Zone;
  spawnedAtMs: number;
  data: TData;
};

/** What a subclass returns from {@link TargetDrillRuntime.spawnTarget}. */
export type SpawnedTarget<TData> = {
  group: THREE.Group;
  /** Materials faded out (opacity → 0) during both the hit and the miss death animations. */
  fadeMats: THREE.MeshBasicMaterial[];
  /** Materials recolored to the miss color on a miss — a subset of `fadeMats` (e.g. ring + dot). */
  missColorMats: THREE.MeshBasicMaterial[];
  data: TData;
};

/** What a subclass returns when a frame (or a timeout) resolves the live target. */
export type Resolution = {
  /** Hit (clean/sloppy) → scale-up death; miss (wrong/timeout) → gray-out death. */
  hit: boolean;
  /** Extra materials to fade with the dying group (e.g. the cut wrong-direction X). */
  extraFadeMats?: THREE.MeshBasicMaterial[];
};

type WaitingPhase = { kind: 'waiting'; remainingMs: number };
type AlivePhase<TData> = {
  kind: 'alive';
  group: THREE.Group;
  fadeMats: THREE.MeshBasicMaterial[];
  missColorMats: THREE.MeshBasicMaterial[];
  center: THREE.Vector3;
  zone: Zone;
  spawnedAtMs: number;
  ttlRemainingMs: number;
  data: TData;
};
type DyingPhase = {
  kind: 'dying';
  group: THREE.Group;
  fadeMats: THREE.MeshBasicMaterial[];
  missColorMats: THREE.MeshBasicMaterial[];
  elapsedMs: number;
  totalMs: number;
  hit: boolean;
  /** Initial scale used as the base for the hit-grow animation. */
  baseScale: number;
};
type Phase<TData> = WaitingPhase | AlivePhase<TData> | DyingPhase;

/**
 * Shared driver for the mid-air target drills (arcade cut + arcade thrust — #44). Owns the
 * `waiting → alive → dying` phase machine, spawn cadence, billboarding, the off-screen
 * indicator, the timer / free-mode gate, and session end. The technique-specific pieces are
 * the only seams: building the target mesh, detecting a resolution from a sword frame, scoring
 * a TTL timeout, aggregating stats, and rendering the HUD.
 *
 * The two lifecycles are identical (unlike pell↔targets, which DESIGN.md kept apart), so the
 * machine lives here once and the four divergences stay isolated behind the abstract methods.
 */
export abstract class TargetDrillRuntime<TData> extends DrillRuntimeBase {
  protected readonly tracker: SwordTracker;
  protected userHeightM: number;
  protected sizeFactor: number;
  protected distanceM: number;
  protected ttlMs: number;
  protected spawnDelayMs: number;
  protected paused = false;

  private anchor: SpawnAnchor | null = null;
  private phase: Phase<TData> = { kind: 'waiting', remainingMs: TARGETS_FIRST_SPAWN_DELAY_MS };
  private prevBlade: BladePose | null = null;
  /** Mini-target glyph at the FOV edge pointing to a target spawned outside the view. */
  private readonly indicator = new OffscreenIndicator();

  protected constructor(protected baseOpts: TargetDrillOptions) {
    super(baseOpts);
    this.tracker = new SwordTracker(SWORD_BLADE_LENGTH_M, SWORD_GRIP_LENGTH_M);
    this.userHeightM = baseOpts.userHeightCm / 100;
    this.sizeFactor = baseOpts.sizeFactor;
    this.distanceM = baseOpts.distanceM;
    this.ttlMs = baseOpts.ttlMs;
    this.spawnDelayMs = baseOpts.spawnDelayMs;
    this.indicator.attachTo(baseOpts.scene);
  }

  protected get isFree(): boolean {
    return this.baseOpts.durationMode === DURATION_MODE.free;
  }

  /** The run's difficulty — selects the best/leaderboard board and labels the stats. */
  protected get difficulty(): Difficulty {
    return this.baseOpts.difficulty;
  }

  /**
   * A competitive run is persisted and scored: timed AND a preset difficulty. Free (no timer)
   * and custom runs are practice only — no history, no best, no leaderboard (#43).
   */
  protected get isCompetitive(): boolean {
    return !this.isFree && this.baseOpts.difficulty !== DIFFICULTY.custom;
  }

  // ── Technique seams ─────────────────────────────────────────
  /** Reset per-run accumulators (shots, combo) — called from `start`. */
  protected abstract resetRun(): void;
  /** Build the target mesh + its fade material lists + any technique data (e.g. required direction). */
  protected abstract spawnTarget(center: THREE.Vector3, zone: Zone): SpawnedTarget<TData>;
  /**
   * Inspect a sword frame against the live target. Return a {@link Resolution} when it resolves
   * (after firing feedback, applying scoring, and logging the shot), or null to keep it alive.
   */
  protected abstract resolveFrame(
    now: number,
    frame: SwordFrame,
    prevBlade: BladePose | null,
    alive: AliveTarget<TData>,
  ): Resolution | null;
  /** Score + log a TTL timeout (a hard miss). The base then plays the miss death animation. */
  protected abstract resolveTimeout(now: number, alive: AliveTarget<TData>): void;
  /** Summarize the run for end / persistence. */
  protected abstract aggregateStats(): SessionStats;
  /** Render the wrist HUD for this technique. */
  protected abstract renderHud(nowMs: number): void;
  /** Hook after a persisted (timed) run aggregates — e.g. record the local best. */
  protected onSessionPersisted(): void {}

  // ── Lifecycle ───────────────────────────────────────────────
  start(): void {
    if (!this.isFree) this.timer.start(this.baseOpts.durationMs, performance.now());
    this.paused = false;
    this.tracker.reset();
    this.anchor = captureSpawnAnchor(this.baseOpts.camera);
    this.phase = { kind: 'waiting', remainingMs: TARGETS_FIRST_SPAWN_DELAY_MS };
    this.prevBlade = null;
    this.resetRun();
  }

  protected onControllersChanged(): void {
    this.tracker.reset();
    this.prevBlade = null;
  }

  setUserHeightCm(cm: number): void {
    this.baseOpts.userHeightCm = cm;
    this.userHeightM = cm / 100;
  }

  override pause(): void {
    this.paused = true;
    super.pause();
  }

  override resume(): void {
    this.paused = false;
    super.resume();
  }

  /** Live-update the disc size multiplier. Rescales the currently alive / dying target too. */
  protected setSizeFactor(factor: number): void {
    this.sizeFactor = factor;
    if (this.phase.kind === 'alive' || this.phase.kind === 'dying') {
      const baseScale = this.phase.kind === 'dying' ? this.phase.baseScale : 1;
      this.phase.group.scale.setScalar(baseScale * factor);
    }
  }

  end(): SessionStats {
    const now = performance.now();
    this.timer.end(now);
    this.clearActiveTarget();
    const stats = this.aggregateStats();
    // Free (test) and custom sessions are not persisted — practice only (#43), consistent with
    // pell free mode.
    if (this.isCompetitive) {
      const elapsedMs = this.timer.elapsedMs(now);
      this.persistSession({
        elapsedMs,
        durationMs: this.baseOpts.durationMs,
        userHeightCm: this.baseOpts.userHeightCm,
        interrupted: elapsedMs < this.baseOpts.durationMs,
        stats,
      });
      this.onSessionPersisted();
    }
    return stats;
  }

  tick(): void {
    const dt = this.clock.getDelta();
    const now = performance.now();

    const running = this.isFree ? !this.paused : this.timer.getState() === TIMER_STATE.running;
    if (!running) {
      this.indicator.hide();
      this.renderHud(now);
      return;
    }

    this.advancePhase(dt * 1000, now);

    if (this.phase.kind === 'alive') {
      const frame = this.tracker.sample(this.baseOpts.sword, dt);
      if (frame) {
        const res = this.resolveFrame(now, frame, this.prevBlade, this.phase);
        if (res) this.startDeath(res);
        this.prevBlade = { base: frame.basePos, tip: frame.tipPos };
      }
    } else {
      this.prevBlade = null;
    }

    // Re-read the phase: a resolution may have just turned the target alive → dying, in which
    // case there is no longer a center to point at.
    if (this.phase.kind === 'alive') {
      this.indicator.update(this.baseOpts.camera, this.phase.center);
    } else {
      this.indicator.hide();
    }

    if (!this.isFree && this.timer.isExpired(now)) {
      const stats = this.end();
      this.baseOpts.onEnd(stats);
      return;
    }

    this.renderHud(now);
  }

  private advancePhase(dtMs: number, now: number): void {
    if (this.phase.kind === 'waiting') {
      this.phase.remainingMs -= dtMs;
      if (this.phase.remainingMs <= 0) this.spawnNext(now);
      return;
    }
    if (this.phase.kind === 'alive') {
      // Free (test) mode: targets never auto-miss — they wait until hit.
      if (!this.isFree) {
        this.phase.ttlRemainingMs -= dtMs;
        if (this.phase.ttlRemainingMs <= 0) {
          this.resolveTimeout(now, this.phase);
          this.startDeath({ hit: false });
          return;
        }
      }
      this.faceCamera(this.phase.group);
      return;
    }
    // dying
    this.phase.elapsedMs += dtMs;
    const tNorm = Math.min(1, this.phase.elapsedMs / this.phase.totalMs);
    if (this.phase.hit) {
      const scale = this.phase.baseScale * (1 + (TARGETS_HIT_ANIM_SCALE - 1) * tNorm);
      this.phase.group.scale.setScalar(scale);
    } else {
      for (const m of this.phase.missColorMats) m.color.setHex(TARGETS_MISS_COLOR_HEX);
    }
    for (const m of this.phase.fadeMats) m.opacity = 1 - tNorm;
    this.faceCamera(this.phase.group);
    if (tNorm >= 1) this.disposeAndScheduleNext(this.phase.group);
  }

  private startDeath(res: Resolution): void {
    if (this.phase.kind !== 'alive') return;
    const alive = this.phase;
    const fadeMats = res.extraFadeMats ? [...alive.fadeMats, ...res.extraFadeMats] : alive.fadeMats;
    this.phase = {
      kind: 'dying',
      group: alive.group,
      fadeMats,
      missColorMats: alive.missColorMats,
      elapsedMs: 0,
      totalMs: res.hit ? TARGETS_HIT_ANIM_MS : TARGETS_MISS_ANIM_MS,
      hit: res.hit,
      baseScale: this.sizeFactor,
    };
  }

  private spawnNext(now: number): void {
    if (!this.anchor) return;
    const zone = sampleZone();
    const center = computeSpawnPosition(this.anchor, this.userHeightM, zone, this.distanceM);
    const spawned = this.spawnTarget(center, zone);
    spawned.group.position.copy(center);
    spawned.group.scale.setScalar(this.sizeFactor);
    this.faceCamera(spawned.group);
    this.baseOpts.scene.add(spawned.group);
    this.phase = {
      kind: 'alive',
      group: spawned.group,
      fadeMats: spawned.fadeMats,
      missColorMats: spawned.missColorMats,
      center,
      zone,
      spawnedAtMs: now,
      ttlRemainingMs: this.ttlMs,
      data: spawned.data,
    };
    this.prevBlade = null;
  }

  private disposeAndScheduleNext(group: THREE.Group): void {
    this.baseOpts.scene.remove(group);
    disposeTargetMesh(group);
    this.phase = { kind: 'waiting', remainingMs: this.spawnDelayMs };
  }

  private clearActiveTarget(): void {
    if (this.phase.kind === 'alive' || this.phase.kind === 'dying') {
      this.baseOpts.scene.remove(this.phase.group);
      disposeTargetMesh(this.phase.group);
    }
    this.indicator.hide();
    this.phase = { kind: 'waiting', remainingMs: Number.POSITIVE_INFINITY };
  }

  private faceCamera(group: THREE.Group): void {
    this.baseOpts.camera.getWorldPosition(_camPos);
    group.lookAt(_camPos);
  }

  override dispose(): void {
    this.clearActiveTarget();
    this.indicator.dispose();
    super.dispose();
  }
}
